import {
	createFileRoute,
	Link,
	Navigate,
	useParams,
	useRouteContext,
	useSearch,
	stripSearchParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { differenceInDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useMemo, useTransition } from "react";
import {
	graphql,
	useLazyLoadQuery,
	usePaginationFragment,
	fetchQuery,
} from "react-relay";
import { z } from "zod";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text } from "@/components/text";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { CommentPreview } from "@/ui/comment-preview";
import { MemberBadge } from "@/ui/member-badge";
import { PostPreview } from "@/ui/post-preview";
import type { uUserActivityFragment$key } from "./__generated__/uUserActivityFragment.graphql";
import type { uUserActivityFragmentQuery } from "./__generated__/uUserActivityFragmentQuery.graphql";
import type {
	ActivityContentType,
	uUserQuery,
} from "./__generated__/uUserQuery.graphql";
import type { uUserHeadQuery } from "./__generated__/uUserHeadQuery.graphql";
import { getImageUrl } from "@/utils/getImageUrl";
import { environment } from "@/relay/environment";
import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@/components/ui/tooltip";

const defaultValues = {
	content: "all",
} as const;

export const Route = createFileRoute("/_visibility/_topnav/u/$username")({
	component: RouteComponent,
	loader: ({ context }) => {
		return { club: context.club };
	},
	validateSearch: zodValidator(
		z.object({
			content: z.enum(["posts", "comments", "all"]).default("all"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
	head: async ({ params: { username }, loaderData }) => {
		if (!loaderData?.club) {
			return {};
		}

		const activeDomain = loaderData.club.domains.find((d) => d.enabled)?.domain;
		const clubUrl = activeDomain
			? `https://${activeDomain}`
			: "https://scarf.club";
		const canonicalUrl = `${clubUrl}/u/${username}`;

		const data = await fetchQuery<uUserHeadQuery>(
			environment,
			graphql`
	    query uUserHeadQuery($username: String!, $club: String!) {
	        user(username: $username) {
	          username
						displayName
						bio
	          photo {
	            signedUrl
	          }
	        }
	        club(slug: $club) {
						name
					}
	    }
	  `,
			{ username, club: loaderData.club.slug! },
		).toPromise();

		if (!data?.user) {
			return {};
		}

		const ogImageUrl = data.user.photo?.signedUrl
			? getImageUrl(data.user.photo.signedUrl, {
					format: "png",
					width: 1200,
					height: 630,
					quality: 80,
					maxBytes: 600,
				})
			: undefined;

		const displayName = data.user.displayName || data.user.username;
		const bio = data.user.bio || `Member of ${data.club.name}`;

		const structuredData = {
			"@context": "https://schema.org",
			"@type": "Person",
			name: displayName,
			username: data.user.username,
			description: bio,
			url: canonicalUrl,
			image: ogImageUrl,
			memberOf: {
				"@type": "Organization",
				name: data.club.name,
				url: clubUrl,
			},
		};

		return {
			meta: [
				{
					title: `${displayName} (@${data.user.username}) | ${data.club.name}`,
				},
				{
					name: "description",
					content: bio.slice(0, 160),
				},
				{
					name: "canonical",
					content: canonicalUrl,
				},
				{
					property: "og:type",
					content: "profile",
				},
				{
					property: "og:title",
					content: `${displayName} (@${data.user.username}) | ${data.club.name}`,
				},
				{
					property: "og:description",
					content: bio.slice(0, 160),
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
				{
					property: "profile:username",
					content: data.user.username,
				},
				...(ogImageUrl
					? [
							{
								property: "og:image",
								content: ogImageUrl,
							},
							{
								property: "og:image:secure_url",
								content: ogImageUrl,
							},
							{
								property: "og:image:width",
								content: "1200",
							},
							{
								property: "og:image:height",
								content: "630",
							},
							{
								property: "twitter:image",
								content: ogImageUrl,
							},
						]
					: []),
			],
			links: [
				{
					rel: "canonical",
					href: canonicalUrl,
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(structuredData),
				},
			],
		};
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { username } = useParams({ from: Route.id });
	const { content } = useSearch({ from: Route.id });

	const [isPending, startTransition] = useTransition();

	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const getContentType = (
		activityContent: typeof content,
	): ActivityContentType => {
		switch (activityContent) {
			case "comments":
				return "COMMENT";
			case "posts":
				return "POST";
			default:
				return "ALL";
		}
	};

	const data = useLazyLoadQuery<uUserQuery>(
		graphql`
    query uUserQuery($username: String!, $club: String!, $contentType: ActivityContentType!) {
        user(username: $username) {
          username
          displayName
          avatarFallback
          memberSince(club: $club)
          bio
          photo {
            signedUrl
          }
          points(club: $club)
          badges(club: $club) {
            id
            ...memberBadgeFragment
          }

          ...uUserActivityFragment @arguments(club: $club, contentType: $contentType)
        }
    }
  `,
		{ club: club.slug, username, contentType: getContentType(content) },
	);

	const {
		data: activityData,
		refetch,
		loadNext,
	} = usePaginationFragment<
		uUserActivityFragmentQuery,
		uUserActivityFragment$key
	>(
		graphql`
    fragment uUserActivityFragment on User
    @refetchable(queryName: "uUserActivityFragmentQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 },
       	cursor: { type: "String" }
        club: { type: "String!"}
        contentType: { type: "ActivityContentType!" }
    )
    {
      activities(first: $first, after: $cursor, club: $club, contentType: $contentType)
      @connection(key: "uUser_activities")
      {
        edges {
          node {
            ...on Post {
              id
              ...postPreviewFragment @arguments(club: $club)
            }

            ... on CommentInterface {
              ...commentPreviewFragment
            }

            __typename
          }
        }
        pageInfo {
          hasNextPage
        }
      }
    }
  `,
		data.user,
	);

	const memberSince = useMemo(() => {
		const date = new Date(data.user?.memberSince);

		if (!date) {
			return "Não é membro";
		}

		const days = differenceInDays(new Date(), new Date(date));

		if (days < 1) {
			return "Membro há menos de 1 dia";
		} else if (days === 1) {
			return "Membro há 1 dia";
		} else if (days < 30) {
			return `Membro há ${days} dias`;
		}

		return `Membro desde ${format(date, "MMM 'de' yyyy", { locale: ptBR })}`;
	}, [data.user?.memberSince]);

	useEffect(() => {
		if (
			!isPending &&
			entry?.isIntersecting &&
			activityData?.activities.pageInfo.hasNextPage
		) {
			startTransition(() => {
				loadNext(10);
			});
		}
	}, [
		entry?.isIntersecting,
		isPending,
		loadNext,
		activityData?.activities.pageInfo.hasNextPage,
	]);

	if (!data.user) {
		return <Navigate to="/comunidade" />;
	}

	return (
		<div className="flex justify-center items-center mt-6 w-full px-6 max-w-full overflow-x-hidden">
			<div className="max-w-[1000px] gap-6 w-full flex flex-row justify-between items-start">
				<div className="flex flex-col flex-1 gap-6 justify-center w-full">
					<Card className="p-6 flex w-full flex-col items-start gap-6 lg:hidden">
						<div className="flex flex-row gap-6 justify-start w-full">
							<Avatar className="size-16">
								<AvatarImage src={data.user?.photo?.signedUrl} />
								<AvatarFallback>{data.user?.avatarFallback!}</AvatarFallback>
							</Avatar>
							<div className="flex flex-col items-start flex-1">
								<div className="flex flex-row items-center gap-3">
									<Heading>{data.user?.displayName}</Heading>

									{data.user?.badges.length > 0 && (
										<div className="flex flex-row items-center justify-start gap-1">
											<MemberBadge badge={data.user.badges[0]} />

											{data.user?.badges.length > 1 && (
												<Tooltip>
													<TooltipTrigger asChild>
														<button
															type="button"
															className="text-[10px] font-medium px-2 py-0.5 rounded-sm bg-muted text-foreground"
														>
															+{data.user?.badges.length - 1}
														</button>
													</TooltipTrigger>

													<TooltipContent className="flex flex-col gap-2">
														{data.user.badges.slice(1).map((badge) => (
															<div key={badge.id}>
																<MemberBadge badge={badge} />
															</div>
														))}
													</TooltipContent>
												</Tooltip>
											)}
										</div>
									)}
								</div>

								{data.user?.displayName === `@${data.user?.username}` ? null : (
									<Text>@{data.user.username}</Text>
								)}
							</div>
						</div>

						<Separator />

						<div className="flex items-start justify-center flex-col w-full gap-3">
							<div className="flex flex-row text-sm/5 text-muted-foreground items-center justify-start gap-2">
								<Icon name="CalendarDays" className="size-4" />
								{memberSince}
							</div>

							<div className="flex flex-row text-sm/5 text-muted-foreground items-center justify-start gap-2">
								<Icon name="CircleStar" className="size-4" />
								{data.user?.points || 0} pontos
							</div>
						</div>
					</Card>

					<Card className="px-6 h-[86px] flex flex-row gap-3 items-center justify-between w-full overflow-x-auto">
						<div className="flex flex-row gap-3">
							<Link
								from={Route.fullPath}
								search={(s) => ({ ...s, content: "all" })}
							>
								<Button variant={content === "all" ? "default" : "ghost"}>
									Visão Geral
								</Button>
							</Link>

							<Link
								from={Route.fullPath}
								search={(s) => ({ ...s, content: "posts" })}
							>
								<Button variant={content === "posts" ? "default" : "ghost"}>
									Posts
								</Button>
							</Link>

							<Link
								from={Route.fullPath}
								search={(s) => ({ ...s, content: "comments" })}
							>
								<Button variant={content === "comments" ? "default" : "ghost"}>
									Comentários
								</Button>
							</Link>
						</div>
					</Card>

					{(activityData?.activities.edges?.length || 0) > 0 ? (
						<>
							{activityData?.activities.edges
								.filter(Boolean)
								.map(({ node }) => {
									if (node?.__typename === "Post") {
										return (
											<PostPreview
												key={node.id}
												post={node}
												showSpaceFeedPin
												onUpdate={() => {
													refetch(
														{
															contentType: getContentType(content),
															club: club.slug,
														},
														{ fetchPolicy: "network-only" },
													);
												}}
											/>
										);
									} else if (node.__typename.includes("Comment")) {
										return <CommentPreview comment={node} />;
									}

									return null;
								})}

							<div ref={ref} className="size-1" />

							{isPending && (
								<div className="flex items-center justify-center">
									<Spinner />
								</div>
							)}
						</>
					) : (
						<div className="flex flex-col items-center justify-center gap-6">
							<Icon name="CircleSlash" className="size-16" />

							<span>Ainda não tem nada aqui :/</span>
						</div>
					)}
				</div>

				<div className="flex flex-col gap-6 w-[340px] min-w-[340px] max-w-[340px] max-lg:hidden">
					<Card className="p-6 flex flex-col items-center gap-6">
						<Avatar className="size-16">
							<AvatarImage src={data.user?.photo?.signedUrl} />
							<AvatarFallback>{data.user?.avatarFallback!}</AvatarFallback>
						</Avatar>

						<div className="flex flex-col items-center">
							<Heading>{data.user?.displayName}</Heading>

							{data.user?.displayName === `@${data.user?.username}` ? null : (
								<Text>@{data.user.username}</Text>
							)}
						</div>

						{data.user?.badges.length ? (
							<div className="flex flex-row items-center justify-start gap-2 flex-wrap">
								{data.user?.badges.map((badge) => (
									<MemberBadge key={badge.id} badge={badge} />
								))}
							</div>
						) : null}

						<Separator />

						<div className="flex items-start justify-center flex-col w-full gap-3">
							<div className="flex flex-row text-sm/5 text-muted-foreground items-center justify-start gap-2">
								<Icon name="CalendarDays" className="size-4" />
								{memberSince}
							</div>

							<div className="flex flex-row text-sm/5 text-muted-foreground items-center justify-start gap-2">
								<Icon name="CircleStar" className="size-4" />
								{data.user?.points || 0} pontos
							</div>
						</div>

						<Separator />

						<div className="flex items-start justify-center flex-col w-full">
							<span className="font-medium select-none text-base/6 text-foreground data-disabled:opacity-50 sm:text-sm/6 mb-3">
								Bio
							</span>

							<span
								data-state={data?.user?.bio ? "full" : "empty"}
								className="text-muted-foreground whitespace-pre-line text-base/6"
							>
								{data.user?.bio || "Esse usuário não tem uma bio"}
							</span>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
}
